import {Component, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {HeaderConfig, MenuItem, NavigationConfig} from '@gatewee/components';
import {
  catchError,
  filter,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import {pollWhileVisible} from '@gatewee/utils/helpers/poll-while-visible';
import {BrandingService} from '@gatewee/services/core/branding.service';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {TaskService} from '@gatewee/services/task.service';
import {PublicSettings} from '@gatewee/types/system';
import {AuthService} from '../../_services/auth/auth.service';
import {readProfileRaw} from '@gatewee/services';
import {ProfileSyncService} from '@gatewee/services/profile-sync.service';
import {menuItems as menuItemsConfig} from '../../config/menu-items';
@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
  standalone: false,
})
export class AdminComponent implements OnInit, OnDestroy {
  settings!: PublicSettings;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private settingsService: SettingsService,
    private authService: AuthService,
    private brandingService: BrandingService,
    private router: Router,
    private taskService: TaskService,
    private readonly profileSync: ProfileSyncService,
  ) {}
  toggleVal = false;
  mouseOvered = false;
  recipientType = '';

  isDashboard = false;

  authorizedSettings = false;
  headerConfig: HeaderConfig | null = null;
  navigationConfig: NavigationConfig | null = null;

  menuItems: MenuItem[] = menuItemsConfig.map(item => ({
    ...item,
    subMenu: item.subMenu?.map(subItem => ({...subItem})),
  }));

  async ngOnInit(): Promise<void> {
    this.recipientType =
      JSON.parse(readProfileRaw())?.recipient?.type ?? 'transactional';

    // Start collapsed on mobile devices
    if (window.innerWidth <= 768) {
      this.toggleVal = true;
    }

    if (!this.authorizedSettings) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await this.authService.refreshSettings().then((settings: any) => {
        this.authorizedSettings = settings.authorized;
      });
    }

    // Initialize header config
    const profile = JSON.parse(readProfileRaw());
    const branding = this.settingsService.get(
      'branding',
    ) as PublicSettings['branding'];

    this.headerConfig = {
      profile,
      branding,
      showMyFeesShortcut: true,
      showTaskbarToggle: true,
      tasks: [],
      tasksRoute: '/panel/tasks',
      onMyFeesNavigate: () => {
        void this.router.navigate(['/panel/my-fees']);
      },
      onLogout: () => this.authService.logout(),
      onThemeToggle: () => {
        if (branding) {
          branding.style.theme =
            branding.style.theme === 'dark' ? 'light' : 'dark';
          this.brandingService.init(branding);
        }
      },
      profileMenuAdditionalItems: [
        {
          title: 'nav.menu.privacyDataRequests',
          route: '/panel/support-tickets/new',
          icon: '@phi.lock',
        },
      ],
    };

    // Initialize navigation config with menu items
    this.navigationConfig = {
      profile,
      menuItems: this.recipientType !== 'capitalization' ? this.menuItems : [],
    };

    this.isDashboard = this.router.url === '/panel';
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.isDashboard = e.urlAfterRedirects === '/panel';
      });

    this.authService
      .profile()
      .pipe(take(1))
      .subscribe(fresh => {
        this.headerConfig = {...this.headerConfig!, profile: fresh};
        this.navigationConfig = {...this.navigationConfig!, profile: fresh};
      });

    this.startActiveTasksPolling();

    this.profileSync.profileUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const profile = JSON.parse(readProfileRaw());
        if (this.headerConfig) {
          this.headerConfig = {...this.headerConfig, profile};
        }
        if (this.navigationConfig) {
          this.navigationConfig = {...this.navigationConfig, profile};
        }
      });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startActiveTasksPolling(): void {
    pollWhileVisible({intervalMs: 30_000})
      .pipe(
        switchMap(() =>
          this.taskService
            .getActiveTasks('recipient')
            .pipe(catchError(() => of({records: [], count: 0}))),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({records}) => {
        if (!this.headerConfig) {
          return;
        }
        this.headerConfig = {...this.headerConfig, tasks: records};
      });
  }
}
